/* =====================================================================================
   Studio review: MLR tasks and document comments.

   MLR is not one person approving a PDF. Medical, legal and regulatory each answer a
   different question about the same asset and each can block. The capacity matters more
   than the person: a medical reviewer signing off a legal question is how a claim reaches
   the market with nobody having actually checked it.

   Comments work on an ordinary DOCX or PPTX, not only on a rendered page. A Word document
   has no stable page geometry until it is rendered, and making people wait for a rendition
   pipeline before they can comment would leave the module unusable in the meantime. Where a
   rendition does exist, a comment can carry a page and a normalised point and pin to it.
   ===================================================================================== */

import { BACKEND } from './db';

export const CAPACITIES = ['medical', 'legal', 'regulatory', 'commercial', 'quality', 'author'] as const;
export const SEVERITIES = ['blocker', 'major', 'minor', 'comment', 'praise'] as const;
export const TASK_STATUS = ['open', 'in_progress', 'approved', 'changes_required', 'declined'] as const;

export interface ReviewTask {
  id: string; asset_id: string; version_id: string | null;
  capacity: string; assignee_id: string; assignee_name?: string;
  due_on: string | null; status: string; decided_at: string | null;
  decision_note: string | null;
}

export interface Comment {
  id: string; asset_id: string; version_id: string | null;
  attachment_id: string | null; parent_id: string | null;
  body: string; severity: string; capacity: string | null;
  page: number | null; x: number | null; y: number | null; quoted_text: string | null;
  status: string; author_id: string; author_name?: string;
  created_at: string; resolved_at: string | null; resolution_note: string | null;
}

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}
async function meId() {
  const { currentMember } = await import('./supabase');
  return (await currentMember())?.id ?? null;
}
function tidy(m: string) { return m.replace(/^.*?(?:ERROR|error):\s*/i, '').trim() || m; }

export async function fetchTasks(assetId: string): Promise<ReviewTask[]> {
  if (!live()) return [];
  const { supabase, nameOf } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('studio').from('review_tasks').select('*').eq('asset_id', assetId)
    .order('capacity');
  if (error) throw new Error(error.message);
  const name = await nameOf();
  return (data ?? []).map((t: any) => ({ ...t, assignee_name: name(t.assignee_id) }));
}

export async function fetchMyReviews() {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('studio').from('v_my_reviews').select('*');
  return data ?? [];
}

export async function assignReview(a: {
  assetId: string; versionId?: string | null; assigneeId: string;
  capacity: string; dueOn?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('studio').rpc('assign_review', {
    p_asset_id: a.assetId, p_version_id: a.versionId ?? null,
    p_assignee: a.assigneeId, p_capacity: a.capacity, p_due_on: a.dueOn ?? null,
  });
  if (error) throw new Error(tidy(error.message));
}

export async function decideReview(taskId: string, status: string, note?: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('studio').rpc('decide_review', {
    p_task_id: taskId, p_status: status, p_note: note ?? null,
  });
  if (error) throw new Error(tidy(error.message));
}

export async function fetchComments(assetId: string): Promise<Comment[]> {
  if (!live()) return [];
  const { supabase, nameOf } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('studio').from('comments').select('*').eq('asset_id', assetId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const name = await nameOf();
  return (data ?? []).map((c: any) => ({ ...c, author_name: name(c.author_id) }));
}

export async function addComment(c: {
  assetId: string; versionId?: string | null; attachmentId?: string | null;
  parentId?: string | null; body: string; severity?: string; capacity?: string;
  page?: number | null; quotedText?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('studio').from('comments').insert({
    org_id: await orgId(), asset_id: c.assetId,
    version_id: c.versionId ?? null, attachment_id: c.attachmentId ?? null,
    parent_id: c.parentId ?? null,
    body: c.body, severity: c.severity || 'comment',
    capacity: c.capacity || null, page: c.page ?? null,
    quoted_text: c.quotedText || null,
    author_id: await meId(),
  });
  if (error) throw new Error(tidy(error.message));
}

export async function resolveComment(id: string, status: string, note?: string) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('studio').from('comments').update({
    status, resolution_note: note ?? null,
    resolved_by: await meId(), resolved_at: new Date().toISOString(),
  }).eq('id', id);
  if (error) throw new Error(tidy(error.message));
}
